import { action, makeObservable, observable } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { IPayoutAccountData } from "./InvoiceTypes";
import { ErpType } from "src/network/User";
import { GET_PAYOUTACCOUNT_DATA } from "src/api/cred";
import { GetPayoutAccountData, GetPayoutAccountDataVariables } from "src/api/generated/GetPayoutAccountData";
import { apolloClientInstance } from "src/network/apolloClientInstance";

export enum LOADING_PAYOUT_ACCOUNT_STATE {
    INIT,
    LOADING
}

export class AccountingPayoutAccountStore {
    rootStore: RootStore;

    erpType: ErpType = ErpType.IT2;

    loadingPayoutAccountState: LOADING_PAYOUT_ACCOUNT_STATE = LOADING_PAYOUT_ACCOUNT_STATE.INIT;

    // This is an array because every Accounting Row has its own realestate value
    currentPayoutAccountSearchQueryIT2: string = "";
    selectedPayoutAccountQueryStringIT2: string = "";

    selectablePayoutAccountDataIT2: IPayoutAccountData[] = [];

    triggerFocusNextAccountingInput: { isActive: boolean; index: number } = { isActive: false, index: 0 };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            currentPayoutAccountSearchQueryIT2: observable,
            selectedPayoutAccountQueryStringIT2: observable,
            triggerFocusNextAccountingInput: observable,
            selectablePayoutAccountDataIT2: observable,
            loadingPayoutAccountState: observable,

            reset: action,
            setCurrentPayoutAccountSearchQueryIT2: action,
            setSelectedPayoutAccountQueryStringIT2: action,
            setTriggerFocusNextAccountingInput: action,
            loadPayoutAccountData: action,
            setSelectablePayoutAccountDataIT2: action,
            setLoadingPayoutAccountState: action,
            init: action
        });
    }

    init = () => {
        this.reset();
        this.setSelectablePayoutAccountDataIT2([]);
    };

    reset = () => {
        this.currentPayoutAccountSearchQueryIT2 = "";
        this.selectedPayoutAccountQueryStringIT2 = "";
    };

    // Setters
    setLoadingPayoutAccountState(loadingPayoutAccountState: LOADING_PAYOUT_ACCOUNT_STATE) {
        this.loadingPayoutAccountState = loadingPayoutAccountState;
    }

    setCurrentPayoutAccountSearchQueryIT2(currentPayoutAccountSearchQueryIT2: string) {
        this.currentPayoutAccountSearchQueryIT2 = currentPayoutAccountSearchQueryIT2;
    }

    setSelectedPayoutAccountQueryStringIT2(selectedPayoutAccountQueryStringIT2: string) {
        this.selectedPayoutAccountQueryStringIT2 = selectedPayoutAccountQueryStringIT2;
    }

    setSelectablePayoutAccountDataIT2(selectablePayoutAccountDataIT2: IPayoutAccountData[]) {
        this.selectablePayoutAccountDataIT2 = selectablePayoutAccountDataIT2;
    }

    /*  This function triggers the focus on the next Account field after selecting a Payout Account
        For IT2 this will always be the Account field in the first accounting row
        For RIMO this will be the Account field in the current accounting row
    */
    setTriggerFocusNextAccountingInput(triggerFocusNextAccountingInput: boolean, accountingRowIndex?: number) {
        const erpType = this.rootStore.authStore.user?.erpType;

        if (erpType === ErpType.IT2) {
            this.triggerFocusNextAccountingInput = {
                isActive: triggerFocusNextAccountingInput,
                index: 0
            };
        }

        if (erpType === ErpType.RIMO && accountingRowIndex !== undefined) {
            this.triggerFocusNextAccountingInput = {
                isActive: triggerFocusNextAccountingInput,
                index: accountingRowIndex
            };
        }
    }

    loadPayoutAccountData = async (realestateId: string): Promise<IPayoutAccountData[]> => {
        this.setLoadingPayoutAccountState(LOADING_PAYOUT_ACCOUNT_STATE.LOADING);

        const { data }: { data: GetPayoutAccountData } = await apolloClientInstance.query<
            GetPayoutAccountData,
            GetPayoutAccountDataVariables
        >({
            query: GET_PAYOUTACCOUNT_DATA,
            variables: {
                realestateid: realestateId
            }
        });

        const payoutAccountData: IPayoutAccountData[] = data.payoutaccounts
            .filter(
                (payoutAccount) =>
                    payoutAccount.iban !== null && payoutAccount.iban !== undefined && payoutAccount.iban !== ""
            )
            .filter((payoutAccount) => payoutAccount.valid)
            .map((payoutAccount) => {
                return {
                    id: payoutAccount.id,
                    name: payoutAccount.name,
                    iban: payoutAccount.iban,
                    valid: payoutAccount.valid,
                    default: payoutAccount.default
                };
            });

        this.setSelectablePayoutAccountDataIT2(payoutAccountData);

        this.setLoadingPayoutAccountState(LOADING_PAYOUT_ACCOUNT_STATE.INIT);

        return payoutAccountData;
    };

    getDefaultPayoutAccount = (): IPayoutAccountData | undefined => {
        if (this.selectablePayoutAccountDataIT2 && this.selectablePayoutAccountDataIT2.length > 0) {
            return this.selectablePayoutAccountDataIT2.find((payoutAccount) => payoutAccount.default);
        }
        return undefined;
    };
}
